import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Col, Typography, Card, Space, Tabs } from 'antd';
import styled from 'styled-components';
import {
  SunOutlined,
  PlusCircleOutlined,
  CalendarOutlined,
  SunFilled,
  MoonFilled,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import DateScroller from './DateScroller';
import SlotCard from './SlotCard';
import { FONT_WEIGHTS, COLORS, TEXT_SIZES } from '../../../utils/constants';
import { FacilitySportDto, SlotDto, SlotSessions } from '../../../types/types';
import { Drawer } from '../../common/Drawer';
import {
  deleteSlotRequest,
  getSlotsByCourtIdRequest,
  reserveSlotsRequest,
  unreserveSlotsRequest,
} from '../../../api/requests/slot';
import { utcToLocalDate } from '../../../utils/dateUtils';
import Button from '../../common/Button';
import CreateSlotModal from './CreateSlotModal';
import UpdateSlotModal from './UpdateSlotModal';
import DeleteModal from '../../shared/DeleteModal';
import { useSnackbar } from '../../../contexts/SnackbarContext/SnackbarProvider';
import ReserveSlotModal from './ReserveSlotModal';
import { SlotStatus } from '../../../enums/enums';
import Spinner from '../../common/Spinner';

interface SlotManagementProps {
  isVisible: boolean;
  onClose: () => void;
  selectedSport: FacilitySportDto;
  facilityName: string;
  courtId: number;
  courtName: string;
  onSlotCreated?: () => void;
}

const getCustomDateDisplay = (date: Dayjs) => {
  const today = dayjs();
  const isToday = date.isSame(today, 'day');
  const isTomorrow = date.isSame(today.add(1, 'day'), 'day');

  return {
    topContent: isToday
      ? 'Today'
      : isTomorrow
        ? 'Tomorrow'
        : date.format('ddd'),
    middleContent: date.format('DD'),
    bottomContent: date.format('MMM'),
  };
};

function isWithinSession(
  time: Date,
  startHour: number,
  endHour: number,
): boolean {
  const hour = time.getHours();
  return hour >= startHour && hour < endHour;
}

const categorizeSlotsBySession = (slots: SlotDto[]): SlotSessions => {
  const sessions: SlotSessions = [
    {
      title: 'Early Morning (4 AM - 6 AM)',
      slots: [],
      icon: <EarlyMorningIcon />,
    },
    {
      title: 'Morning (6 AM - 12 PM)',
      slots: [],
      icon: <MorningIcon />,
    },
    {
      title: 'Afternoon (12 PM - 5 PM)',
      slots: [],
      icon: <AfternoonIcon />,
    },
    {
      title: 'Evening (5 PM - 8 PM)',
      slots: [],
      icon: <EveningIcon />,
    },
    {
      title: 'Night (8 PM - 12 AM)',
      slots: [],
      icon: <NightIcon />,
    },
    {
      title: 'Mid Night (12 AM - 4 AM)',
      slots: [],
      icon: <MidNightIcon />,
    },
  ];

  slots.forEach((slot) => {
    const startTime = utcToLocalDate(slot.startTime);

    if (isWithinSession(startTime, 4, 6)) {
      sessions[0].slots.push(slot); // Early Morning
    } else if (isWithinSession(startTime, 6, 12)) {
      sessions[1].slots.push(slot); // Morning
    } else if (isWithinSession(startTime, 12, 17)) {
      sessions[2].slots.push(slot); // Afternoon
    } else if (isWithinSession(startTime, 17, 20)) {
      sessions[3].slots.push(slot); // Evening
    } else if (isWithinSession(startTime, 20, 24)) {
      sessions[4].slots.push(slot); // Night
    } else if (isWithinSession(startTime, 0, 4)) {
      sessions[5].slots.push(slot); // Midnight
    }
  });

  return sessions;
};

export enum SlotTab {
  Available = 'availableSlots',
  Reserved = 'reservedSlots',
  Booked = 'bookedSlots',
}

const SlotManagement: React.FC<SlotManagementProps> = ({
  isVisible,
  onClose,
  selectedSport,
  facilityName,
  courtId,
  courtName,
  onSlotCreated,
}) => {
  const snackbar = useSnackbar();
  const [isCreateSlot, setIsCreateSlot] = useState<boolean>(false);
  const [isUpdateSlot, setIsUpdateSlot] = useState<boolean>(false);
  const [isDeleteSlot, setIsDeleteSlot] = useState<boolean>(false);
  const [selectedSlotForEdit, setSelectedSlotForEdit] =
    useState<SlotDto | null>(null);
  const [deletingSlotId, setDeletingSlotId] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const courts = selectedSport.courts || [];
  const [slots, setSlots] = useState<SlotSessions | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openSlotModal, setOpenSlotModal] = useState<boolean>(false);
  const [reserveSlotId, setReserveSlotId] = useState<number | null>(null);
  const [slotExpireTime, setSlotExpireTime] = useState<string | null>(null);
  const [reserve, setReserve] = useState<boolean | null>(null);
  const [slotData, setSlotData] = useState<{
    available: SlotSessions;
    reserved: SlotSessions;
    booked: SlotSessions;
  }>({
    available: [],
    reserved: [],
    booked: [],
  });
  const [activeTab, setActiveTab] = useState<SlotTab>(SlotTab.Available);
  const tabMapping = useMemo<Record<SlotTab, SlotSessions>>(
    () => ({
      [SlotTab.Available]: slotData.available,
      [SlotTab.Reserved]: slotData.reserved,
      [SlotTab.Booked]: slotData.booked,
    }),
    [slotData],
  );
  const tabItems = useMemo(
    () => [
      {
        key: SlotTab.Available,
        label: `Available Slots (${slotData.available.reduce(
          (acc, session) => acc + session.slots.length,
          0,
        )})`,
      },
      {
        key: SlotTab.Reserved,
        label: `Reserved Slots (${slotData.reserved.reduce(
          (acc, session) => acc + session.slots.length,
          0,
        )})`,
      },
      {
        key: SlotTab.Booked,
        label: `Booked Slots (${slotData.booked.reduce(
          (acc, session) => acc + session.slots.length,
          0,
        )})`,
      },
    ],
    [slotData],
  );

  const handleDateChange = (date: Dayjs) => {
    setSelectedDate(date);
  };

  const handleCloseSlotCreation = () => {
    setIsCreateSlot(false);
  };

  const handleSlotCreation = () => {
    setIsCreateSlot(false);
    if (courtId) {
      fetchSlots(courtId);
    }
    onSlotCreated?.();
  };

  const onEditSlot = (slotData: SlotDto) => {
    setSelectedSlotForEdit(slotData);
    setIsUpdateSlot(true);
  };

  const handleCloseSlotUpdate = () => {
    setIsUpdateSlot(false);
    setSelectedSlotForEdit(null);
  };

  const handleSlotUpdate = () => {
    setIsUpdateSlot(false);
    setSelectedSlotForEdit(null);
    if (courtId) {
      fetchSlots(courtId);
    }
  };

  const onDeleteSlot = (slotId: number) => {
    setDeletingSlotId(slotId);
    setIsDeleteSlot(true);
  };

  const handleDeleteSlot = async () => {
    if (!deletingSlotId) return;
    try {
      const response = await deleteSlotRequest(deletingSlotId);
      if (response?.isSuccess) {
        setDeletingSlotId(null);
        setIsDeleteSlot(false);
        if (courtId) {
          fetchSlots(courtId);
        }
        snackbar.success('Slot deleted successfully');
      } else {
        setDeletingSlotId(null);
        setIsDeleteSlot(false);
        snackbar.error(response?.error);
      }
    } catch (error: any) {
      snackbar.error(error?.Error || 'Error deleting slot');
    }
  };

  const handleCancelDeleteSlot = () => {
    setDeletingSlotId(null);
    setIsDeleteSlot(false);
  };

  const onConfirm = (
    slotId: number,
    slotExpireTime: string,
    reserve: boolean,
  ) => {
    setReserveSlotId(slotId);
    setSlotExpireTime(slotExpireTime);
    setOpenSlotModal(true);
    setReserve(reserve);
  };

  const handleReserveSlotCancel = () => {
    setOpenSlotModal(false);
    setReserveSlotId(null);
    setSlotExpireTime(null);
    setReserve(null);
  };

  const handleReserveSlot = async (note: string) => {
    if (!reserveSlotId) return;
    try {
      const response = await reserveSlotsRequest({
        slotIds: [reserveSlotId],
        note,
        expiresAt: slotExpireTime || undefined,
      });
      if (response?.isSuccess) {
        snackbar.success('Slot reserved successfully');
        setOpenSlotModal(false);
        if (courtId) {
          fetchSlots(courtId);
        }
      }
    } catch (error: any) {
      snackbar.error(error.Error || 'Error reserving slot');
    } finally {
      setReserveSlotId(null);
      setSlotExpireTime(null);
      setReserve(null);
    }
  };

  const handleUnReserveSlot = async () => {
    if (!reserveSlotId) return;
    try {
      const response = await unreserveSlotsRequest({
        slotIds: [reserveSlotId],
      });
      if (response?.isSuccess) {
        snackbar.success('Slot unreserved successfully');
        setOpenSlotModal(false);
        if (courtId) {
          fetchSlots(courtId);
        }
      }
    } catch (error: any) {
      snackbar.error(error.Error || 'Error unreserving slot');
    } finally {
      setReserveSlotId(null);
      setSlotExpireTime(null);
      setReserve(null);
    }
  };

  const handleTabChange = useCallback((key: string) => {
    setActiveTab(key as SlotTab);
  }, []);

  const startOfDayUTC = useMemo(
    () => selectedDate.hour(0).minute(0).second(0).millisecond(0).toISOString(),
    [selectedDate],
  );

  const endOfDayUTC = useMemo(
    () =>
      selectedDate
        .hour(23)
        .minute(59)
        .second(59)
        .millisecond(999)
        .toISOString(),
    [selectedDate],
  );

  const fetchSlots = useCallback(
    async (courtId: number) => {
      try {
        setIsLoading(true);
        const response = await getSlotsByCourtIdRequest(courtId, {
          startDate: startOfDayUTC,
          endDate: endOfDayUTC,
        });
        if (response?.isSuccess && response?.value) {
          const sessionSlots = categorizeSlotsBySession(response.value);
          const availableSlots = sessionSlots?.map((session) => ({
            ...session,
            slots: session.slots.filter(
              (slot) => slot.slotStatus === SlotStatus.Available,
            ),
          }));

          const reservedSlots = sessionSlots?.map((session) => ({
            ...session,
            slots: session.slots.filter(
              (slot) => slot.slotStatus === SlotStatus.Reserved,
            ),
          }));

          const bookedSlots = sessionSlots?.map((session) => ({
            ...session,
            slots: session.slots.filter(
              (slot) => slot.slotStatus === SlotStatus.Booked,
            ),
          }));

          if (availableSlots && reservedSlots && bookedSlots)
            setSlotData({
              available: availableSlots,
              reserved: reservedSlots,
              booked: bookedSlots,
            });
        }
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        console.error('Error fetching slots', error);
      }
    },
    [startOfDayUTC, endOfDayUTC],
  );

  useEffect(() => {
    if (courtId) {
      fetchSlots(courtId);
    }
  }, [courtId, selectedDate, fetchSlots]);

  useEffect(() => {
    setSlots(tabMapping[activeTab]);
  }, [activeTab, slotData]);

  return (
    <>
      <Drawer
        title="Create Booking Slots"
        width={'70vw'}
        onClose={onClose}
        open={isVisible}
      >
        {isLoading ? (
          <Spinner fullScreen={true} minHeight={400} />
        ) : (
          <>
            <Col span={24}>
              <HeaderActionBar>
                <HeaderInfo>
                  <FacilityName>{facilityName}</FacilityName>
                  <Description>
                    Create custom time slots for customer bookings
                  </Description>
                </HeaderInfo>
                <PriceInfo>
                  <BasePriceText>
                    Base price:
                    <StyledSpan>
                      {' '}
                      Rs {selectedSport.regularHourlyPrice}/h
                    </StyledSpan>
                  </BasePriceText>
                  <WeekendPriceText>
                    Weekend price:
                    <StyledSpan>
                      {' '}
                      Rs {selectedSport.weekendHourlyPrice}/h
                    </StyledSpan>
                  </WeekendPriceText>
                </PriceInfo>
                <Button
                  type="primary"
                  size="large"
                  icon={<StyledPlusCircleOutlined />}
                  onClick={() => setIsCreateSlot(true)}
                  isDisabled={selectedDate.isBefore(dayjs(), 'day')}
                >
                  Create Slot Pattern
                </Button>
              </HeaderActionBar>
            </Col>

            <Row gutter={[16, 24]}>
              <Col>
                <SportTitle>
                  {selectedSport.sportName} - {courtName}
                </SportTitle>
              </Col>
              <Col span={24}>
                <Card>
                  <CalendarLabel>
                    <StyledCalendarOutlined /> Select Date to View Slots
                  </CalendarLabel>
                  <DatePickerWrapper>
                    <DateScroller
                      selectedDate={selectedDate}
                      onDateSelect={handleDateChange}
                      customDisplay={getCustomDateDisplay}
                    />
                  </DatePickerWrapper>
                </Card>
              </Col>

              <Col span={24}>
                <Card>
                  {slotData.available.some(
                    (session) => session.slots.length !== 0,
                  ) ||
                  slotData.reserved.some(
                    (session) => session.slots.length !== 0,
                  ) ||
                  slotData.booked.some(
                    (session) => session.slots.length !== 0,
                  ) ? (
                    <Row justify="space-between">
                      <StyledSpace direction="vertical">
                        <CalendarLabel>
                          <StyledCalendarOutlined /> Created Slots for
                          {selectedDate.format('dddd, MMMM D, YYYY')}
                        </CalendarLabel>
                        <SubText>
                          All slots created for customer bookings on this date
                        </SubText>
                      </StyledSpace>
                      <Tabs
                        items={tabItems}
                        activeKey={activeTab}
                        onChange={handleTabChange}
                      />
                    </Row>
                  ) : (
                    <EmptyText>No slots created for this day</EmptyText>
                  )}
                  {slots?.some((session) => session.slots.length !== 0) ? (
                    slots.map(
                      (session) =>
                        session.slots.length > 0 && (
                          <SlotSection key={session.title}>
                            <SectionHeader>
                              {session.icon}
                              <SectionTitle>{session.title}</SectionTitle>
                            </SectionHeader>
                            <SlotWrapper>
                              {session.slots.map(
                                (slot: SlotDto, index: number) => (
                                  <SlotCard
                                    key={index}
                                    slot={slot}
                                    onEdit={() => onEditSlot(slot)}
                                    onDelete={(slotId) => onDeleteSlot(slotId)}
                                    onConfirm={(slotId, expireTime, reserve) =>
                                      onConfirm(slotId, expireTime, reserve)
                                    }
                                  />
                                ),
                              )}
                            </SlotWrapper>
                          </SlotSection>
                        ),
                    )
                  ) : (
                    <EmptySlotsMessage>{`No ${activeTab.replace(/Slots$/i, '')} slots`}</EmptySlotsMessage>
                  )}
                </Card>
              </Col>
            </Row>
            {isCreateSlot && courtId && selectedDate && (
              <CreateSlotModal
                visible={isCreateSlot}
                onClose={handleCloseSlotCreation}
                onSubmit={handleSlotCreation}
                courtName={
                  courts.find((court) => court.courtId === courtId)
                    ?.courtName || 'Court'
                }
                courtId={courtId}
                selectedDate={selectedDate}
              />
            )}
            {isUpdateSlot && selectedSlotForEdit && selectedDate && (
              <UpdateSlotModal
                visible={isUpdateSlot}
                onClose={handleCloseSlotUpdate}
                onSubmit={handleSlotUpdate}
                courtName={selectedSlotForEdit.courtName}
                courtId={selectedSlotForEdit.courtId}
                selectedDate={selectedDate}
                slotData={selectedSlotForEdit}
              />
            )}

            {isDeleteSlot && deletingSlotId && (
              <DeleteModal
                open={isDeleteSlot}
                moduleType="Slot"
                onConfirm={handleDeleteSlot}
                onCancel={handleCancelDeleteSlot}
              />
            )}
          </>
        )}

        {openSlotModal && reserveSlotId && reserve !== null && (
          <ReserveSlotModal
            open={openSlotModal}
            onCancel={handleReserveSlotCancel}
            onConfirm={reserve ? handleReserveSlot : handleUnReserveSlot}
            reserve={reserve}
          />
        )}
      </Drawer>
    </>
  );
};

const DatePickerWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const HeaderActionBar = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  border-radius: 8px;
  background-color: ${COLORS.white.primary};
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const FacilityName = styled.h2`
  margin: 0;
  font-size: ${TEXT_SIZES.MD}px;
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
`;

const Description = styled.p`
  margin: 0;
  font-size: ${TEXT_SIZES.SM}px;
  color: ${COLORS.grey.secondary};
`;

const PriceInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-right: 16px;
`;

const BasePriceText = styled.span`
  font-size: ${TEXT_SIZES.SM}px;
  text-align: left;
`;

const WeekendPriceText = styled.span`
  font-size: ${TEXT_SIZES.SM}px;
`;

const StyledSpan = styled.span`
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  color: ${COLORS.blue.primary};
`;

const SportTitle = styled(Typography.Text)`
  font-size: ${TEXT_SIZES.LG}px;
  font-weight: ${FONT_WEIGHTS.BOLD};
`;

const CalendarLabel = styled(Typography.Text)`
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  display: block;
  margin-bottom: 12px;
`;

const SubText = styled(Typography.Text)`
  color: ${COLORS.grey.secondary};
  margin-bottom: 14px;
`;

const SlotSection = styled.div`
  margin-top: 24px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  color: ${COLORS.blue.primary};
`;

const SectionTitle = styled(Typography.Text)`
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  font-size: ${TEXT_SIZES.MD}px;
`;

const SlotWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const EmptyText = styled.div`
  font-size: ${TEXT_SIZES.EIGHT_XL}px;
  font-weight: ${FONT_WEIGHTS.BOLD};
  color: ${COLORS.grey.secondary};
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
`;

const StyledSpace = styled(Space)`
  marginbottom: 24px;
  gap: 2px;
`;

const EmptySlotsMessage = styled.div`
  font-size: ${TEXT_SIZES.LG}px;
  font-weight: ${FONT_WEIGHTS.BOLD};
  color: ${COLORS.grey.secondary};
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  margin-top: 20px;
`;

const EarlyMorningIcon = styled(SunOutlined)`
  font-size: ${TEXT_SIZES.BASE}px;
  color: ${COLORS.orange.primary};
`;

const MorningIcon = styled(SunFilled)`
  font-size: ${TEXT_SIZES.BASE}px;
  color: ${COLORS.orange.primary};
`;

const AfternoonIcon = styled(SunFilled)`
  font-size: ${TEXT_SIZES.BASE}px;
  color: ${COLORS.brown.primary};
`;

const EveningIcon = styled(SunFilled)`
  font-size: ${TEXT_SIZES.BASE}px;
  color: ${COLORS.orange.primary};
`;

const NightIcon = styled(MoonFilled)`
  font-size: ${TEXT_SIZES.BASE}px;
  color: ${COLORS.blue.secondary};
`;

const MidNightIcon = styled(MoonFilled)`
  font-size: ${TEXT_SIZES.BASE}px;
  color: ${COLORS.blue.primary};
`;

const StyledCalendarOutlined = styled(CalendarOutlined)`
  font-size: ${TEXT_SIZES.BASE}px;
`;

const StyledPlusCircleOutlined = styled(PlusCircleOutlined)`
  font-size: ${TEXT_SIZES.LG}px;
`;

export default SlotManagement;
